export enum AdminRoleEnum {
  Administrator = 0, // Control total del sistema (Aitor y Else)
  Supervisor = 1, // Acceso a TODO excepto Facturación (Eldys)
  EditorManager = 2, // Jefa de redacción - Gestiona redactores (María)
  Editor = 3, // Redactor - Ve solo pedidos asignados
  Journalist = 4, // Periodista - Ve solo pedidos asignados con categorías específicas
  Outreach = 5, // Gestión de medios (Alfredo y Yerlika)
  Accountant = 6, // Acceso solo a facturación (Patricia)
  Commercial = 7, // Comercial interno - solo backoffice, asignable a clientes
}

export const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'Dólar estadounidense (USD)' },
  { value: 'MXN', label: 'Peso mexicano (MXN)' },
  { value: 'ARS', label: 'Peso argentino (ARS)' },
  { value: 'COP', label: 'Peso colombiano (COP)' },
  { value: 'CLP', label: 'Peso chileno (CLP)' },
  { value: 'PEN', label: 'Sol peruano (PEN)' },
  { value: 'GBP', label: 'Libra esterlina (GBP)' },
];

export type UserModel = {
  id: number;
  name: string;
  surname: string;
  email: string;
  photoUrl?: string;
  role?: AdminRoleEnum;
  managerId?: number;
  isActive?: boolean;
  ordersAssignedCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
  manager?: UserModel;
  journalistCategories?: JournalistCategoryModel[];
  categoryIds?: number[]; // Para facilitar el manejo de categorías en formularios

  // Campos específicos para periodistas
  twitterProfile?: string;
  facebookProfile?: string;
  linkedInProfile?: string;
  instagramProfile?: string;
  referenceUrls?: string;
  writingLanguage?: string;
  contactEmail?: string;
  biography?: string;
  pricePer600Words?: number;
  pricePer800Words?: number;
  pricePer1000Words?: number;
  currency?: string;
  phoneNumber?: string;
  pendingValidation?: boolean;

  // Campos específicos para comerciales
  notes?: string;
  negotiatedPercentage?: number;
};

export type JournalistCategoryModel = {
  id: number;
  adminUserId: number;
  categoryId: number;
  categoryName?: string;
  createdAt: Date;
};

export enum WorkspaceEnum {
  EditorBoss = 'editor-boss', // Dashboard para jefes de redacción
  Editors = 'editors', // Dashboard específico para redactores
  Journalists = 'journalists', // Dashboard específico para periodistas
  Supervisor = 'supervisor', // Dashboard para supervisores
  Outreach = 'outreach', // Dashboard para outreach
  Accountant = 'accountant', // Dashboard para contables
  Administrator = 'administrator', // Dashboard completo para administradores
}

export const ROLE_DISPLAY_NAMES: Record<AdminRoleEnum, string> = {
  [AdminRoleEnum.Administrator]: 'Administrador',
  [AdminRoleEnum.Supervisor]: 'Supervisor',
  [AdminRoleEnum.EditorManager]: 'Jefa de Redacción',
  [AdminRoleEnum.Editor]: 'Redactor',
  [AdminRoleEnum.Journalist]: 'Periodista',
  [AdminRoleEnum.Outreach]: 'Outreach',
  [AdminRoleEnum.Accountant]: 'Contable',
  [AdminRoleEnum.Commercial]: 'Comercial',
};

/**
 * Configuración de workspaces por rol
 */
export const ROLE_DEFAULT_WORKSPACE: Record<AdminRoleEnum, WorkspaceEnum> = {
  [AdminRoleEnum.Administrator]: WorkspaceEnum.Administrator,
  [AdminRoleEnum.Supervisor]: WorkspaceEnum.Supervisor,
  [AdminRoleEnum.EditorManager]: WorkspaceEnum.EditorBoss,
  [AdminRoleEnum.Editor]: WorkspaceEnum.Editors,
  [AdminRoleEnum.Journalist]: WorkspaceEnum.Journalists, // Assuming Journalist uses the Editors workspace
  [AdminRoleEnum.Outreach]: WorkspaceEnum.Outreach,
  [AdminRoleEnum.Accountant]: WorkspaceEnum.Accountant,
  // Commercial no hace login, no usa workspace. Asignamos Administrator como fallback.
  [AdminRoleEnum.Commercial]: WorkspaceEnum.Administrator,
};

export type CreateAdminUserModel = {
  name: string;
  surname: string;
  email: string;
  role: AdminRoleEnum;
  /** Opcional cuando role === Commercial (el backend autogenera). Requerido para los demás. */
  password?: string;
  managerId?: number;
  isActive?: boolean;
  categoryIds?: number[]; // Para periodistas

  // Campos específicos para comerciales
  notes?: string;
  negotiatedPercentage?: number;

  // Campos específicos para periodistas
  twitterProfile?: string;
  facebookProfile?: string;
  linkedInProfile?: string;
  instagramProfile?: string;
  referenceUrls?: string;
  writingLanguage?: string;
  contactEmail?: string;
  biography?: string;
  pricePer600Words?: number;
  pricePer800Words?: number;
  pricePer1000Words?: number;
  currency?: string;
  phoneNumber?: string;
  pendingValidation?: boolean;
};

export type UpdateAdminUserModel = {
  name?: string;
  surname?: string;
  email?: string;
  password?: string; // Campo opcional para cambio de contraseña
  role?: AdminRoleEnum;
  managerId?: number;
  isActive?: boolean;
  categoryIds?: number[]; // Para periodistas

  // Campos específicos para comerciales
  notes?: string;
  negotiatedPercentage?: number;

  // Campos específicos para periodistas
  twitterProfile?: string;
  facebookProfile?: string;
  linkedInProfile?: string;
  instagramProfile?: string;
  referenceUrls?: string;
  writingLanguage?: string;
  contactEmail?: string;
  biography?: string;
  pricePer600Words?: number;
  pricePer800Words?: number;
  pricePer1000Words?: number;
  currency?: string;
  phoneNumber?: string;
  pendingValidation?: boolean;
};

/**
 * Datos básicos del comercial (para el dropdown de asignación a clientes).
 */
export type CommercialOptionModel = {
  id: number;
  name: string;
  surname: string;
  negotiatedPercentage?: number;
};
